import { forwardRef, useId, useMemo, useState } from 'react';
import { Check, Flag, Heart, Map as MapIcon, Menu, Navigation2, Search, Trophy, X, type LucideIcon } from 'lucide-react';
import type { Museum } from '../data/types';
import { useT } from '../hooks/useT';
import { isBezocht, zoekMusea, type StatusMap } from '../utils/museum';
import { chip } from '../utils/ui';
import type { TranslationKey } from '../utils/i18n';

export type Tab = 'kaart' | 'buurt' | 'voortgang';
export const TABS: readonly Tab[] = ['kaart', 'buurt', 'voortgang'];
export type Filter = 'alles' | 'niet' | 'wens' | 'fav' | 'bezocht';
export const FILTERS: readonly Filter[] = ['alles', 'niet', 'wens', 'fav', 'bezocht'];

const TAB_INFO: Record<Tab, { key: TranslationKey; Icon: LucideIcon }> = {
  kaart: { key: 'tabKaart', Icon: MapIcon },
  buurt: { key: 'tabBuurt', Icon: Navigation2 },
  voortgang: { key: 'tabVoortgang', Icon: Trophy },
};

const FILTER_INFO: Record<Filter, { key: TranslationKey; Icon?: LucideIcon }> = {
  alles: { key: 'filterAlles' },
  niet: { key: 'filterNietBezocht' },
  bezocht: { key: 'filterBezocht', Icon: Check },
  wens: { key: 'filterWens', Icon: Flag },
  fav: { key: 'filterFav', Icon: Heart },
};

interface KopProps {
  tab: Tab;
  setTab: (t: Tab) => void;
  filter: Filter;
  setFilter: (f: Filter) => void;
  musea: Museum[];
  status: StatusMap;
  aantalZichtbaar: number;
  onKies: (m: Museum) => void;
  onMenu: () => void;
}

/** Zwevende kop: titel + menu, tabschakelaar, en op de kaart ook zoeken en filters. */
export const Kop = forwardRef<HTMLDivElement, KopProps>(function Kop(
  { tab, setTab, filter, setFilter, musea, status, aantalZichtbaar, onKies, onMenu },
  ref,
) {
  const { t, tf } = useT();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [actief, setActief] = useState(0);
  const lijstId = useId();
  const resultaten = useMemo(() => zoekMusea(musea, query), [musea, query]);
  const toonLijst = open && query.trim().length > 0;

  const kies = (m: Museum) => {
    onKies(m);
    setQuery('');
    setOpen(false);
    (document.activeElement as HTMLElement | null)?.blur();
  };

  return (
    <div ref={ref} className="pointer-events-none fixed inset-x-0 top-0 z-20 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:p-4">
      <div className="pointer-events-auto mx-auto max-w-xl space-y-2 rounded-3xl bg-slate-50/85 p-2 shadow-lg backdrop-blur-md dark:border dark:border-white/10 dark:bg-slate-900/85">
        <header className="flex items-center gap-2 pl-2">
          <h1 className="flex-1 text-lg font-bold">{t('appHeading')}</h1>
          <button
            onClick={onMenu}
            aria-label={t('menuOpen')}
            aria-haspopup="dialog"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-black/5 transition-all duration-150 active:scale-95 dark:bg-slate-800/80"
          >
            <Menu size={22} aria-hidden />
          </button>
        </header>

        <nav aria-label={t('tabsLabel')} className="grid grid-cols-3 gap-1 rounded-2xl bg-black/5 p-1 dark:bg-slate-800/80">
          {TABS.map((id) => {
            const { key, Icon } = TAB_INFO[id];
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                aria-current={tab === id ? 'page' : undefined}
                className={`flex min-h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-2 text-sm font-semibold transition-all duration-150 active:scale-95 ${
                  tab === id ? 'bg-slate-900 text-slate-50 shadow-sm dark:bg-slate-100 dark:text-slate-900' : ''
                }`}
              >
                <Icon size={16} aria-hidden /> {t(key)}
              </button>
            );
          })}
        </nav>

        {tab === 'kaart' && (
          <>
            <div className="relative">
              <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 dark:text-slate-400" aria-hidden />
              <input
                type="search"
                role="combobox"
                aria-label={t('zoekLabel')}
                aria-expanded={toonLijst}
                aria-controls={lijstId}
                aria-autocomplete="list"
                aria-activedescendant={toonLijst && resultaten[actief] ? `${lijstId}-${actief}` : undefined}
                placeholder={t('zoekPlaceholder')}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                  setActief(0);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => window.setTimeout(() => setOpen(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') setActief((i) => Math.min(i + 1, resultaten.length - 1));
                  else if (e.key === 'ArrowUp') setActief((i) => Math.max(i - 1, 0));
                  else if (e.key === 'Enter' && resultaten[actief]) kies(resultaten[actief]);
                  else if (e.key === 'Escape') {
                    e.stopPropagation();
                    setQuery('');
                  } else return;
                  e.preventDefault();
                }}
                className="min-h-12 w-full rounded-2xl border-none bg-black/5 pl-11 pr-12 text-base outline-none placeholder:text-slate-600 focus-visible:ring-2 focus-visible:ring-indigo-600 dark:bg-slate-800/80 dark:placeholder:text-slate-400 [&::-webkit-search-cancel-button]:hidden"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  aria-label={t('zoekWissen')}
                  className="absolute right-0.5 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-2xl"
                >
                  <X size={18} aria-hidden />
                </button>
              )}
              {toonLijst && (
                <ul
                  id={lijstId}
                  role="listbox"
                  aria-label={t('zoekLabel')}
                  className="absolute inset-x-0 top-full z-10 mt-2 max-h-[50dvh] overflow-y-auto rounded-2xl bg-slate-50 p-1 shadow-xl dark:border dark:border-white/10 dark:bg-slate-900"
                >
                  {resultaten.length === 0 && <li className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{t('zoekGeen')}</li>}
                  {resultaten.map((m, i) => (
                    <ZoekResultaat
                      key={m.id}
                      id={`${lijstId}-${i}`}
                      museum={m}
                      actief={i === actief}
                      bezocht={isBezocht(status[m.id])}
                      onKies={() => kies(m)}
                    />
                  ))}
                </ul>
              )}
            </div>

            <div role="group" aria-label={t('filterLabel')} className="-mx-2 flex gap-2 overflow-x-auto px-2 pb-0.5 [scrollbar-width:none]">
              {FILTERS.map((id) => {
                const { key, Icon } = FILTER_INFO[id];
                return (
                  <button key={id} onClick={() => setFilter(id)} aria-pressed={filter === id} className={chip(filter === id)}>
                    {Icon && <Icon size={16} aria-hidden />} {t(key)}
                  </button>
                );
              })}
            </div>
            <p className="sr-only" aria-live="polite">{tf('filterAantal', { n: aantalZichtbaar })}</p>
          </>
        )}
      </div>
    </div>
  );
});

function ZoekResultaat({ id, museum, actief, bezocht, onKies }: { id: string; museum: Museum; actief: boolean; bezocht: boolean; onKies: () => void }) {
  return (
    <li
      id={id}
      role="option"
      aria-selected={actief}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onKies}
      className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl px-3 py-2 ${actief ? 'bg-black/5 dark:bg-white/10' : ''}`}
    >
      <span
        aria-hidden
        className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-[2.5px] ${
          bezocht ? 'border-emerald-700 bg-emerald-700 text-white dark:border-emerald-500 dark:bg-emerald-500' : 'border-indigo-700 dark:border-indigo-400'
        }`}
      >
        {bezocht && <Check size={12} strokeWidth={3.5} />}
      </span>
      <span className="min-w-0">
        <span className="block truncate font-semibold">{museum.naam}</span>
        <span className="block text-sm text-slate-600 dark:text-slate-400">{museum.plaats}</span>
      </span>
    </li>
  );
}
